import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminStaffPage() {
  useSeo({ title: "Staff | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "staff"],
    queryFn: async () => (await api.get("/admin/staff")).data,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "moderator", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [newStaff, setNewStaff] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      setNewStaff(data);
      setForm({ name: "", email: "", role: "moderator", password: "" });
      await refetch();
      client.invalidateQueries({ queryKey: ["admin"] });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/staff/${s.id}`, { active: !s.active });
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-staff-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="adm-label">Staff</p>
          <h1 className="mt-2 text-3xl font-semibold text-admin-text">People with staff access</h1>
          <p className="mt-1 text-sm text-admin-muted">New staff must set up 2FA on first sign-in.</p>
        </div>
        <button onClick={() => { setAddOpen(true); setNewStaff(null); }} className="adm-btn adm-btn-primary" data-testid="admin-staff-add-button">Add staff member</button>
      </header>
      <section className="adm-card overflow-hidden">
        {isLoading ? <p className="p-5 text-sm text-admin-muted">Loading...</p> : (
          <table className="w-full text-sm">
            <thead className="bg-admin-surface2 text-xs uppercase text-admin-muted">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Name</th>
                <th className="px-4 py-2 text-left font-semibold">Email</th>
                <th className="px-4 py-2 text-left font-semibold">Role</th>
                <th className="px-4 py-2 text-left font-semibold">Last sign-in</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-admin-border">
              {data?.staff?.map((s) => (
                <tr key={s.id} data-testid="admin-staff-row">
                  <td className="px-4 py-2 text-admin-text">{s.name}</td>
                  <td className="px-4 py-2 text-admin-text">{s.email}</td>
                  <td className="px-4 py-2 text-admin-text">{s.role_label}</td>
                  <td className="px-4 py-2 text-xs text-admin-muted">{s.last_login_at ? fmtDate(s.last_login_at, true) : "—"}</td>
                  <td className="px-4 py-2"><span className={`adm-pill ${s.active ? "border-[#1F7A4D]/40 bg-[#E6F4EC] text-[#1F7A4D]" : "border-admin-border bg-admin-surface2 text-admin-muted"}`}>{s.active ? "Active" : "Deactivated"}</span></td>
                  <td className="px-4 py-2"><button onClick={() => toggleActive(s)} className="adm-btn adm-btn-outline" data-testid="admin-staff-toggle">{s.active ? "Deactivate" : "Reactivate"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="adm-card border-admin-border bg-admin-surface sm:max-w-md" data-testid="admin-staff-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-admin-text">Add staff member</DialogTitle>
            <DialogDescription className="text-admin-muted">Share the TOTP secret privately. It will not be shown again.</DialogDescription>
          </DialogHeader>
          {newStaff ? (
            <div className="space-y-3">
              <p className="text-sm text-admin-text">Share this setup code securely:</p>
              <code className="block break-all rounded-[8px] border border-admin-border bg-admin-surface2 p-3 font-mono text-sm text-admin-text" data-testid="admin-staff-new-secret">{newStaff.totp_secret}</code>
              <img src={newStaff.qr} alt="TOTP QR" className="mx-auto h-48 w-48 rounded border border-admin-border bg-white p-2" />
              <button onClick={() => { setAddOpen(false); setNewStaff(null); }} className="adm-btn adm-btn-primary w-full" data-testid="admin-staff-close">Done</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="adm-label">Name</label>
                <input required className="adm-input mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-staff-name-input" />
              </div>
              <div>
                <label className="adm-label">Email</label>
                <input type="email" required className="adm-input mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-staff-email-input" />
              </div>
              <div>
                <label className="adm-label">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="adm-input mt-1.5 h-[38px] justify-between" data-testid="admin-staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(data?.roles || []).map((r) => <SelectItem key={r.slug} value={r.slug}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="adm-label">Initial password (10+ chars)</label>
                <input required minLength={10} type="text" className="adm-input mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-staff-password-input" />
              </div>
              {error && <p className="text-sm text-[#B42318]" role="alert">{error}</p>}
              <button type="submit" disabled={busy} className="adm-btn adm-btn-primary w-full" data-testid="admin-staff-submit">
                {busy ? "Creating..." : "Create account"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
